/**
 * EDIT PROFILE MODAL
 * 
 * Allows users to edit their profile:
 * - Upload avatar image (with drag-to-position cropping)
 * - Upload banner image (with drag-to-position cropping)
 * - Edit display name
 * - Edit bio
 * - Add social media links
 * 
 * Features:
 * - ImageCropper integration for positioning images before upload
 * - Auto-compresses images to reduce file size
 * - 30-second upload timeout to prevent hanging on slow connections
 * - Scroll-to-error so users always see error messages
 * 
 * Images are uploaded to Firebase Storage
 * Profile data is saved to Firestore
 */

import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Upload, Save, User, FileText, Instagram, Youtube, Globe, Link } from 'lucide-react';
import ImageCropper from './ImageCropper';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { ACCEPTED_IMAGE_TYPES, convertHeicToJpeg, isHeicFile } from '../utils/imageUtils';

interface SocialLinks {
  instagram?: string;
  youtube?: string;
  tiktok?: string;
  facebook?: string;
  blog?: string;
  shop?: string;
}

interface EditProfileModalProps {
  currentProfile: {
    displayName: string;
    bio: string;
    avatarUrl: string;
    bannerUrl: string;
    socialLinks?: SocialLinks;
  };
  onClose: () => void;
  onSave: (updatedProfile: {
    displayName: string;
    bio: string;
    avatarUrl: string;
    bannerUrl: string;
    socialLinks: SocialLinks;
  }) => void;
}

export default function EditProfileModal({ currentProfile, onClose, onSave }: EditProfileModalProps) {
  const [displayName, setDisplayName] = useState(currentProfile.displayName || '');
  const [bio, setBio] = useState(currentProfile.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(currentProfile.avatarUrl || '');
  const [bannerUrl, setBannerUrl] = useState(currentProfile.bannerUrl || '');
  
  // Social links state
  const [instagram, setInstagram] = useState(currentProfile.socialLinks?.instagram || '');
  const [youtube, setYoutube] = useState(currentProfile.socialLinks?.youtube || '');
  const [tiktok, setTiktok] = useState(currentProfile.socialLinks?.tiktok || '');
  const [facebook, setFacebook] = useState(currentProfile.socialLinks?.facebook || '');
  const [blog, setBlog] = useState(currentProfile.socialLinks?.blog || '');
  const [shop, setShop] = useState(currentProfile.socialLinks?.shop || '');
  
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Cropper state
  const [cropperImage, setCropperImage] = useState<{ file: File; type: 'avatar' | 'banner' } | null>(null);
  
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  
  // Scroll to error when it changes
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Handle avatar file selection - opens cropper
  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (including HEIC)
    if (!file.type.startsWith('image/') && !isHeicFile(file)) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 15MB before cropping - will be compressed after)
    if (file.size > 15 * 1024 * 1024) {
      setError('Image must be less than 15MB');
      return;
    }

    setError('');

    // Convert HEIC to JPEG before cropping (cropper can't read HEIC)
    const processedFile = await convertHeicToJpeg(file);

    // Open cropper with processed file
    setCropperImage({ file: processedFile, type: 'avatar' });

    // Reset the input so the same file can be selected again if needed
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };

  // Handle banner file selection - opens cropper
  const handleBannerSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (including HEIC)
    if (!file.type.startsWith('image/') && !isHeicFile(file)) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 20MB before cropping - will be compressed after)
    if (file.size > 20 * 1024 * 1024) {
      setError('Banner image must be less than 20MB');
      return;
    }

    setError('');

    // Convert HEIC to JPEG before cropping (cropper can't read HEIC)
    const processedFile = await convertHeicToJpeg(file);

    // Open cropper with processed file
    setCropperImage({ file: processedFile, type: 'banner' });
    
    // Reset the input so the same file can be selected again if needed
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
  };

  // Handle cropped image from ImageCropper
  const handleCropConfirm = (croppedBlob: Blob, previewUrl: string) => {
    if (!cropperImage) return;
    
    // Create a File from the Blob for upload
    const fileName = cropperImage.type === 'avatar' ? 'avatar.jpg' : 'banner.jpg';
    const croppedFile = new File([croppedBlob], fileName, { type: 'image/jpeg' });
    
    if (cropperImage.type === 'avatar') {
      setAvatarFile(croppedFile);
      setAvatarPreview(previewUrl);
    } else {
      setBannerFile(croppedFile);
      setBannerPreview(previewUrl);
    }
    
    setCropperImage(null);
  };

  // Handle cropper cancel
  const handleCropCancel = () => {
    setCropperImage(null);
  };

  // Upload image to Firebase Storage with timeout
  const uploadImage = async (file: File, path: string): Promise<string> => {
    // Create a promise that rejects after 30 seconds
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Upload timed out. Please check your connection and try again.')), 30000);
    });
    
    // Race between upload and timeout
    const uploadPromise = async () => {
      const imageRef = ref(storage, path);
      // Callers always pass a path with timestamp + safeName, so the URL is
      // permanent — long-cache it everywhere downstream.
      await uploadBytes(imageRef, file, {
        cacheControl: 'public, max-age=31536000, immutable',
        contentType: 'image/jpeg',
      });
      return await getDownloadURL(imageRef);
    };
    
    return Promise.race([uploadPromise(), timeoutPromise]);
  };

  // Handle save
  const handleSave = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      setError('You must be logged in to update your profile');
      return;
    }

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      let newAvatarUrl = avatarUrl;
      let newBannerUrl = bannerUrl;

      // Upload avatar if changed
      if (avatarFile) {
        setIsUploading(true);
        const timestamp = Date.now();
        const safeName = avatarFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        newAvatarUrl = await uploadImage(
          avatarFile, 
          `users/${currentUser.uid}/profile/avatar_${timestamp}_${safeName}`
        );
      }

      // Upload banner if changed
      if (bannerFile) {
        setIsUploading(true);
        const timestamp = Date.now();
        const safeName = bannerFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        newBannerUrl = await uploadImage(
          bannerFile, 
          `users/${currentUser.uid}/profile/banner_${timestamp}_${safeName}`
        );
      }

      setIsUploading(false);

      // Build social links object (only include non-empty values)
      const socialLinks: SocialLinks = {};
      if (instagram.trim()) socialLinks.instagram = instagram.trim();
      if (youtube.trim()) socialLinks.youtube = youtube.trim();
      if (tiktok.trim()) socialLinks.tiktok = tiktok.trim();
      if (facebook.trim()) socialLinks.facebook = facebook.trim();
      if (blog.trim()) socialLinks.blog = blog.trim();
      if (shop.trim()) socialLinks.shop = shop.trim();

      // Update Firestore - update both root level and nested profile for compatibility
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: displayName.trim(),
        bio: bio.trim(),
        avatarUrl: newAvatarUrl,
        bannerUrl: newBannerUrl,
        socialLinks: socialLinks,
        'profile.displayName': displayName.trim(),
        'profile.bio': bio.trim(),
        'profile.avatarUrl': newAvatarUrl,
        'profile.bannerUrl': newBannerUrl,
        'profile.socialLinks': socialLinks,
        updatedAt: new Date()
      });

      // Call onSave callback with updated data
      onSave({
        displayName: displayName.trim(),
        bio: bio.trim(),
        avatarUrl: newAvatarUrl,
        bannerUrl: newBannerUrl,
        socialLinks: socialLinks
      });

      onClose();
    } catch (err) {
      console.error('Error saving profile:', err);
      // Show specific error message if available
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to save profile. Please try again.');
      }
      setIsUploading(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Remove avatar
  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setAvatarUrl('');
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };

  // Remove banner
  const handleRemoveBanner = () => {
    setBannerFile(null);
    setBannerPreview(null);
    setBannerUrl('');
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
  };

  // Get displayed avatar (preview or existing)
  const displayedAvatar = avatarPreview || avatarUrl;
  const displayedBanner = bannerPreview || bannerUrl;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="modal-light max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border border-gray-300 px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <h2 className="text-xl font-bold text-light-primary font-heading">
            Edit Profile
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-default"
          >
            <X className="icon-md text-light-muted" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div 
              ref={errorRef}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
            >
              {error}
            </div>
          )}

          {/* Banner Upload */}
          <div>
            <label className="block text-sm font-medium text-light-secondary mb-2">
              <span className="flex items-center gap-2">
                🖼️ Profile Banner
              </span>
            </label>
            <div 
              className="relative w-full h-32 bg-gradient-to-r from-fuchsia-400 to-purple-500 rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => bannerInputRef.current?.click()}
            >
              {displayedBanner ? (
                <img 
                  src={displayedBanner} 
                  alt="Banner preview" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-white/60 text-sm">Click to upload banner</span>
                </div>
              )}
              
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className="bg-white/90 rounded-full p-3">
                  <Camera className="icon-base text-light-secondary" />
                </div>
              </div>

              {/* Remove button */}
              {displayedBanner && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemoveBanner(); }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-default"
                  title="Remove banner"
                >
                  <X className="icon-sm" />
                </button>
              )}
            </div>
            <input
              ref={bannerInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              onChange={handleBannerSelect}
              className="hidden"
            />
            <p className="text-xs text-light-muted mt-1">Recommended: 1500x500 pixels. Max 10MB.</p>
          </div>

          {/* Avatar Upload */}
          <div>
            <label className="block text-sm font-medium text-light-secondary mb-2">
              <span className="flex items-center gap-2">
                📷 Profile Photo
              </span>
            </label>
            <div className="flex items-center gap-4">
              <div 
                className="relative w-24 h-24 rounded-full rainbow-gradient overflow-hidden cursor-pointer group flex-shrink-0"
                onClick={() => avatarInputRef.current?.click()}
              >
                {displayedAvatar ? (
                  <img
                    src={displayedAvatar}
                    alt="Avatar preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl text-gray-400">
                    💅
                  </div>
                )}
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="icon-base text-white" />
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-default text-sm font-medium"
                >
                  <Upload className="icon-sm" />
                  Upload Photo
                </button>
                {displayedAvatar && (
                  <button
                    type="button"
                    onClick={handleRemoveAvatar}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-light-primary rounded-lg hover:bg-gray-50 transition-default text-sm"
                  >
                    <X className="icon-sm" />
                    Remove
                  </button>
                )}
              </div>
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              onChange={handleAvatarSelect}
              className="hidden"
            />
            <p className="text-xs text-light-muted mt-2">Square image recommended. Max 5MB.</p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-light-secondary mb-2">
              <span className="flex items-center gap-2">
                <User className="icon-sm" />
                Display Name
              </span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={50}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all font-body"
              placeholder="Your display name"
            />
            <p className="text-xs text-light-muted mt-1 text-right">{displayName.length}/50</p>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-light-secondary mb-2">
              <span className="flex items-center gap-2">
                <FileText className="icon-sm" />
                Bio
              </span>
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all resize-none font-body"
              placeholder="Tell others about yourself and your nail art journey..."
            />
            <p className="text-xs text-light-muted mt-1 text-right">{bio.length}/500</p>
          </div>

          {/* Social Media Links */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-light-secondary">
              <span className="flex items-center gap-2">
                <Link className="icon-sm" />
                Social Media Links
              </span>
            </label>
            <p className="text-xs text-light-muted -mt-2">Add links to your social profiles. Leave blank to hide.</p>

            {/* Instagram */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center flex-shrink-0">
                <Instagram className="icon-md text-white" />
              </div>
              <input
                type="text"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all text-sm font-body"
                placeholder="@yourusername"
              />
            </div>

            {/* YouTube */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center flex-shrink-0">
                <Youtube className="icon-md text-white" />
              </div>
              <input
                type="text"
                value={youtube}
                onChange={(e) => setYoutube(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all text-sm font-body"
                placeholder="@channel or youtube.com/c/yourchannel"
              />
            </div>

            {/* TikTok */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </div>
              <input
                type="text"
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all text-sm font-body"
                placeholder="@yourusername"
              />
            </div>

            {/* Facebook */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <input
                type="text"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all text-sm font-body"
                placeholder="facebook.com/yourpage"
              />
            </div>

            {/* Blog/Website */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <Globe className="icon-md text-white" />
              </div>
              <input
                type="text"
                value={blog}
                onChange={(e) => setBlog(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all text-sm font-body"
                placeholder="www.yourblog.com"
              />
            </div>

            {/* Shop */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
              </div>
              <input
                type="text"
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all text-sm font-body"
                placeholder="yourshop.etsy.com"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border border-gray-300">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-6 py-3 border border-gray-200 text-light-primary rounded-xl font-semibold hover:bg-gray-50 transition-default disabled:opacity-50 font-body"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !displayName.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-xl font-semibold hover:bg-slate-900 transition-default disabled:opacity-50 disabled:cursor-not-allowed font-body"
            >
              {isSaving ? (
                <>
                  {isUploading ? 'Uploading...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Save className="icon-md" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Image Cropper Modal */}
      {cropperImage && (
        <ImageCropper
          imageFile={cropperImage.file}
          aspectRatio={cropperImage.type === 'avatar' ? 1 : 4}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          maxOutputWidth={cropperImage.type === 'avatar' ? 400 : 1200}
          quality={0.85}
          title={cropperImage.type === 'avatar' ? 'Position Your Avatar' : 'Position Your Banner'}
        />
      )}
    </div>
  );
}
